import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { onAuthStateChanged, signOut } from "firebase/auth";
import { collection, getDocs } from 'firebase/firestore';
import { auth, db } from './services/firebase';
import { SearchProvider } from './context/SearchContext';

import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import BottomNav from './components/layout/BottomNav';
import AuthModal from './components/common/AuthModal';
import { AboutPage, ContactPage, ForBusinessesPage, PrivacyPolicyPage, TermsOfServicePage } from './pages/StaticPages';

import './App.css';

// Lazy Load Pages
const HomePage = lazy(() => import('./pages/HomePage'));
const DirectoryPage = lazy(() => import('./pages/DirectoryPage'));
const DirectoryCategoryPage = lazy(() => import('./pages/DirectoryCategoryPage'));
const GuidesPage = lazy(() => import('./pages/GuidesPage'));
const GuideDetailPage = lazy(() => import('./pages/GuideDetailPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const NewsPage = lazy(() => import('./pages/NewsPage'));
const NewsDetailPage = lazy(() => import('./pages/NewsDetailPage'));
const MapPage = lazy(() => import('./pages/MapPage'));
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const BusinessDetailPage = lazy(() => import('./pages/BusinessDetailPage'));
const CommunityPage = lazy(() => import('./pages/CommunityPage'));
const PostDetailPage = lazy(() => import('./pages/PostDetailPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminRecommendationsPage = lazy(() => import('./pages/AdminRecommendationsPage'));
const MetaGuidePage = lazy(() => import('./pages/MetaGuidePage'));
const CreateAdPage = lazy(() => import('./pages/CreateAdPage'));
const ClassifiedsSearchPage = lazy(() => import('./pages/ClassifiedsSearchPage'));

// Admin UID - REPLACE THIS WITH YOUR ACTUAL ADMIN UID FROM FIREBASE CONSOLE
const ADMIN_UID = "YOUR_ADMIN_UID_HERE";

// GA4 Measurement ID - Replace with yours from Google Analytics console
const GA_MEASUREMENT_ID = "G-L77TJ6SP3Q";

// Tracking component to log page views
const AnalyticsTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (GA_MEASUREMENT_ID !== "G-XXXXXXXXXX") {
      ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
    }
    // Also scroll to top on route change
    window.scrollTo(0, 0);
  }, [location]);
  return null;
};

// Loading Spinner Component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#3D550C]"></div>
  </div>
);

export default function App() {
  const [user, setUser] = useState(null);
  const [authModalMode, setAuthModalMode] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [savedEvents, setSavedEvents] = useState([]);
  const [joinedEvents, setJoinedEvents] = useState([]);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    // Initialize GA4
    if (GA_MEASUREMENT_ID !== "G-XXXXXXXXXX") {
      ReactGA.initialize(GA_MEASUREMENT_ID);
    }

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        fetchFavorites(currentUser.uid);
        fetchSavedEvents(currentUser.uid);
        fetchJoinedEvents(currentUser.uid);
        // Check if user is admin (Simple hardcoded check for MVP)
        if (currentUser.email === 'tonyseverine@gmail.com' || currentUser.email === 'tonyseverines@gmail.com' || currentUser.uid === ADMIN_UID) {
          setIsAdmin(true);
        } else {
          setIsAdmin(false);
        }
      } else {
        setFavorites([]);
        setSavedEvents([]);
        setJoinedEvents([]);
        setIsAdmin(false);
      }
      setAuthInitialized(true);
    });
    return () => unsubscribe();
  }, []);

  const fetchFavorites = async (uid) => {
    try {
      const favoritesCollection = collection(db, "users", uid, "favorites");
      const querySnapshot = await getDocs(favoritesCollection);
      const favoriteIds = querySnapshot.docs.map(doc => doc.id);
      setFavorites(favoriteIds);
    } catch (error) { console.error("Error fetching favorites:", error); }
  };

  const fetchSavedEvents = async (uid) => {
    try {
      const eventsCollection = collection(db, "users", uid, "saved_events");
      const querySnapshot = await getDocs(eventsCollection);
      const eventIds = querySnapshot.docs.map(doc => doc.id);
      setSavedEvents(eventIds);
    } catch (error) { console.error("Error fetching saved events:", error); }
  };

  const fetchJoinedEvents = async (uid) => {
    try {
      const joinedCollection = collection(db, "users", uid, "joined_events");
      const querySnapshot = await getDocs(joinedCollection);
      const eventIds = querySnapshot.docs.map(doc => doc.id);
      setJoinedEvents(eventIds);
    } catch (error) { console.error("Error fetching joined events:", error); }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) { console.error("Error signing out: ", error); }
  };

  if (!authInitialized) return <div className="text-center p-10">Loading...</div>;

  return (
    <Router>
      <SearchProvider>
        <AnalyticsTracker />
        <div style={{ backgroundColor: '#F1F0EA', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          {authModalMode && (<AuthModal mode={authModalMode} onClose={() => setAuthModalMode(null)} onAuthSuccess={() => console.log("Auth successful!")} />)}

          <Header
            user={user}
            isAdmin={isAdmin}
            onLoginClick={() => setAuthModalMode('login')}
            onSignupClick={() => setAuthModalMode('signup')}
            onLogoutClick={handleLogout}
          />

          <main className="container mx-auto px-4 lg:px-6 py-12 pb-24 lg:pb-12 flex-grow">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HomePage user={user} favorites={favorites} setFavorites={setFavorites} />} />
                <Route path="/directory" element={<DirectoryPage user={user} favorites={favorites} setFavorites={setFavorites} />} />
                <Route path="/directory/:slug" element={<DirectoryCategoryPage user={user} favorites={favorites} setFavorites={setFavorites} />} />
                <Route path="/events" element={<EventsPage user={user} savedEvents={savedEvents} setSavedEvents={setSavedEvents} joinedEvents={joinedEvents} setJoinedEvents={setJoinedEvents} />} />
                <Route path="/news" element={<NewsPage />} />
                <Route path="/news/:id" element={<NewsDetailPage />} />
                <Route path="/guides" element={<GuidesPage />} />
                <Route path="/guides/:slug" element={<GuideDetailPage />} />
                <Route path="/map" element={<MapPage />} />
                <Route path="/favorites" element={user ? <FavoritesPage user={user} favorites={favorites} setFavorites={setFavorites} savedEvents={savedEvents} setSavedEvents={setSavedEvents} joinedEvents={joinedEvents} setJoinedEvents={setJoinedEvents} /> : <Navigate to="/" />} />
                <Route path="/profile" element={user ? <ProfilePage /> : <Navigate to="/" />} />
                <Route path="/business/:id" element={<BusinessDetailPage user={user} />} />
                
                {/* Community Routes */}
                <Route path="/community" element={<CommunityPage user={user} />} />
                <Route path="/community/:id" element={<PostDetailPage user={user} />} />

                            {/* Admin Route Protection */}
                            <Route path="/admin" element={isAdmin ? <AdminDashboard /> : <Navigate to="/" />} />
                            <Route path="/admin/recommendations" element={isAdmin ? <AdminRecommendationsPage user={user} /> : <Navigate to="/" />} />

                            {/* Ads Route */}
                            <Route path="/create-ad" element={user ? <CreateAdPage user={user} /> : <Navigate to="/" />} />

                            {/* Classifieds Route */}
                            <Route path="/classifieds" element={<ClassifiedsSearchPage />} />

                            <Route path="/meta-guide" element={<MetaGuidePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/for-businesses" element={<ForBusinessesPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsOfServicePage />} />

                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </Suspense>
          </main>

          <BottomNav user={user} onLoginClick={() => setAuthModalMode('login')} />
          <Footer />
        </div>
      </SearchProvider>
    </Router>
  );
}